import equal from "fast-deep-equal";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { GridRowId, GridRowSelectionModel } from "@mui/x-data-grid-premium";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { ValidatedTextField } from "components/common/inputs/validatedTextField";
import { useNotifyStatus } from "contexts/status";
import { useTenantData } from "contexts/tenant";
import { isFetchBaseQueryError, sorted } from "helpers";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { useDefaultMaterialLimits } from "hooks/useDefaultMaterialLimits";
import { useDefaultTargetBaskets } from "hooks/useDefaultTargetBaskets";
import { useNumberSerialiser } from "hooks/serialisers/numbers";
import { useTenant } from "hooks/settings";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  MaterialLimit,
  MaterialLimitBounds,
  MaterialLimitRead,
  useGetFormattedBasketConstraintsQuery,
  useGetMaterialMetadataSetQuery,
  useGetMaterialPhysicsSetQuery,
  useGetPhysicalParametersSetQuery,
  useListMaterialConstraintClassesQuery,
  useUpdateDefaultMaterialLimitsMutation,
} from "src/store/api/generatedApi";
import {
  coefficientSignature,
  isMaterialLimitNullified,
  materialLimitKey,
} from "src/utils/materialLimitOverrides";
import { ConflictWarningBanner } from "./ConflictWarningBanner";
import { useDefaultParametersContext } from "./context";
import { indexConflicts } from "./parameterConflicts";
import { useMaterialLimitFeasibility } from "./useMaterialLimitFeasibility";
import {
  activeBoundFieldsByColumn,
  boundForScope,
  buildChefGroupRows,
  deriveLimitColumns,
  differsFromScrapchefDefault,
  buildLimitLabelsBySignature,
  formatSignatureLabel,
  MATERIAL_LIMIT_BOUND_FIELDS,
  MaterialLimitBoundField,
  MaterialLimitColumn,
  MaterialLimitScope,
} from "./materialLimitMatrix";
import { InequalityLayout, slotInputSx } from "./MaterialLimitInequality";
import { MaterialLimitLegend } from "./MaterialLimitLegend";
import {
  MaterialLimitMeasure,
  measureUnitType,
  useMeasureSerialiser,
} from "./materialLimitMeasure";
import { MaterialLimitsGrid } from "./MaterialLimitsGrid";
import { ScrapChefDefaultsWarning } from "./ScrapChefDefaultsWarning";

const toMaterialLimit = (read: MaterialLimitRead): MaterialLimit => ({
  chef_group_id: read.chef_group_id,
  coefficients: read.coefficients,
  name: read.name,
  hardness: read.hardness,
  bounds: read.bounds,
});

const withUpdatedField = (
  limit: MaterialLimit,
  scope: MaterialLimitScope,
  field: MaterialLimitBoundField | "hardness",
  value: number | null
): MaterialLimit => {
  if (field === "hardness") {
    return value == null ? limit : { ...limit, hardness: value };
  }
  const existing = boundForScope(limit, scope);
  const updated: MaterialLimitBounds = {
    basket_id: scope,
    min_mass: existing?.min_mass ?? null,
    soft_min_mass: existing?.soft_min_mass ?? null,
    soft_max_mass: existing?.soft_max_mass ?? null,
    max_mass: existing?.max_mass ?? null,
    [field]: value,
  };
  const isEmpty =
    updated.min_mass == null &&
    updated.max_mass == null &&
    updated.soft_min_mass == null &&
    updated.soft_max_mass == null;
  const others = limit.bounds.filter(
    (bound) => (bound.basket_id ?? null) !== scope
  );
  return { ...limit, bounds: isEmpty ? others : [...others, updated] };
};

interface Props {
  isActive: boolean;
  setConfirmOpen: (confirmOpen: boolean) => void;
}

export const MaterialLimitDefaultParameters = ({
  isActive,
  setConfirmOpen,
}: Props) => {
  const { setTabActions, setHasMaterialLimitChanges } =
    useDefaultParametersContext();
  const tenantName = useTenant();
  const { t } = useTenantTranslation();
  const notifyStatus = useNotifyStatus();
  const {
    activeSet,
    chefGroupsById,
    scrapchefLimitsByKey,
    showScrapchefWarning,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useDefaultMaterialLimits();
  const [updateDefaults, { isLoading: isSaving }] =
    useUpdateDefaultMaterialLimitsMutation();

  const originalLimits = useMemo(
    () => activeSet?.material_limits.map(toMaterialLimit) ?? null,
    [activeSet]
  );
  const [workingLimits, setWorkingLimits] = useState<MaterialLimit[] | null>(
    null
  );
  // Reset the working copy whenever the loaded set changes (initial load and
  // after a save refetch) — the React-recommended "adjust state on prop change"
  // pattern, rather than a state-setting effect.
  const [syncedFrom, setSyncedFrom] = useState<MaterialLimit[] | null>(null);
  if (syncedFrom !== originalLimits) {
    setSyncedFrom(originalLimits);
    setWorkingLimits(originalLimits);
  }

  const originalByKey = useMemo(
    () => new Map((originalLimits ?? []).map((l) => [materialLimitKey(l), l])),
    [originalLimits]
  );
  const editedIds = useMemo(() => {
    const ids = new Set<string>();
    for (const limit of workingLimits ?? []) {
      const original = originalByKey.get(materialLimitKey(limit));
      if (!equal(original, limit)) {
        ids.add(materialLimitKey(limit));
      }
    }
    return ids;
  }, [workingLimits, originalByKey]);
  const removedCount =
    (originalLimits?.length ?? 0) - (workingLimits?.length ?? 0);
  const hasChanges = editedIds.size > 0 || removedCount !== 0;
  useEffect(() => {
    setHasMaterialLimitChanges(hasChanges);
  }, [hasChanges, setHasMaterialLimitChanges]);

  // Check per-basket capacity against the persisted default basket counts.
  const {
    rows: targetBasketRows,
    isLoading: targetBasketsLoading,
    isFetching: targetBasketsFetching,
  } = useDefaultTargetBaskets();
  // Wait for basket data. An empty list means that no baskets are configured.
  const targetBaskets = useMemo(
    () =>
      targetBasketsLoading || targetBasketsFetching
        ? null
        : targetBasketRows.map((row) => ({
            chef_group_id: row.id,
            target_num_baskets: row.defaultTargetNumBaskets,
          })),
    [targetBasketRows, targetBasketsLoading, targetBasketsFetching]
  );
  const {
    conflicts,
    isChecking,
    hasError: feasibilityError,
  } = useMaterialLimitFeasibility({
    workingLimits,
    targetBaskets,
  });
  const conflictIndex = useMemo(() => indexConflicts(conflicts), [conflicts]);
  const chefGroupNameById = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(chefGroupsById).map(([id, info]) => [
          Number(id),
          info.name,
        ])
      ),
    [chefGroupsById]
  );

  const [scope, setScope] = useState<MaterialLimitScope>(null);
  const [hardnessView, setHardnessView] = useState(false);

  // Display unit. Values stay in mass; the serialiser converts for display and
  // parses edits back to mass. Percent divides by the default charged-mass
  // lower limit, so it is only offered when that limit is set.
  const { default_mix_breakdown: defaultMixBreakdown } = useTenantData();
  const { data: physicalParametersSet } = useGetPhysicalParametersSetQuery({
    tenantName,
    physicalParametersSetId: "default",
  });
  const chargedMassLower =
    physicalParametersSet?.parameters.target_charged_mass_lower ?? null;
  const percentAvailable = chargedMassLower !== null;
  const units = useUnitsFormatter(false);
  const [measure, setMeasure] = useState<MaterialLimitMeasure>(() =>
    defaultMixBreakdown === "small_mass"
      ? "small_mass"
      : defaultMixBreakdown === "percentage"
        ? "percent"
        : "mass"
  );
  const effectiveMeasure: MaterialLimitMeasure =
    measure === "percent" && !percentAvailable ? "mass" : measure;
  const boundSerialiser = useMeasureSerialiser(
    effectiveMeasure,
    chargedMassLower
  );

  const { data: basketConstraints } = useGetFormattedBasketConstraintsQuery({
    tenantName,
  });
  const basketScopes = useMemo(
    () =>
      sorted(basketConstraints ?? [], (basket) => basket.basket_number).flatMap(
        (basket) =>
          basket.basket_id == null
            ? []
            : [{ id: basket.basket_id, number: basket.basket_number }]
      ),
    [basketConstraints]
  );
  const basketIds = useMemo(
    () => basketScopes.map((basket) => basket.id),
    [basketScopes]
  );
  const basketNumberById = useMemo(
    () =>
      Object.fromEntries(
        basketScopes.map((basket) => [basket.id, basket.number])
      ),
    [basketScopes]
  );

  const effectiveScope =
    scope !== null && basketIds.includes(scope) ? scope : null;

  const columns = useMemo(
    () => deriveLimitColumns(activeSet?.material_limits ?? []),
    [activeSet]
  );
  const rows = useMemo(
    () => buildChefGroupRows(workingLimits ?? [], chefGroupsById),
    [workingLimits, chefGroupsById]
  );
  const activeFieldsBySignature = useMemo(
    () => activeBoundFieldsByColumn(workingLimits ?? [], effectiveScope),
    [workingLimits, effectiveScope]
  );

  const { data: materialMetadataSet } = useGetMaterialMetadataSetQuery({
    tenantName,
    materialMetadataSetId: "default",
  });
  const { data: materialPhysicsSet } = useGetMaterialPhysicsSetQuery({
    tenantName,
    materialPhysicsSetId: "default",
  });
  const { data: constraintClasses } = useListMaterialConstraintClassesQuery(
    materialMetadataSet && materialPhysicsSet
      ? {
          tenantName,
          materialMetadataSetId: materialMetadataSet.id,
          materialPhysicsSetId: materialPhysicsSet.id,
        }
      : skipToken
  );
  const classNameById = useMemo(
    () =>
      (constraintClasses ?? []).reduce<Record<number, string>>(
        (names, constraintClass) => {
          names[constraintClass.id] = constraintClass.name;
          return names;
        },
        {}
      ),
    [constraintClasses]
  );
  const limitLabelBySignature = useMemo(
    () => buildLimitLabelsBySignature(columns, classNameById, t),
    [columns, classNameById, t]
  );

  const onCellChange = useCallback(
    (
      chefGroupId: number,
      column: MaterialLimitColumn,
      field: MaterialLimitBoundField | "hardness",
      value: number | null
    ) => {
      setWorkingLimits((current) =>
        (current ?? []).map((limit) =>
          limit.chef_group_id === chefGroupId &&
          coefficientSignature(limit) === column.signature
            ? withUpdatedField(limit, effectiveScope, field, value)
            : limit
        )
      );
    },
    [effectiveScope]
  );

  const onCellAdd = useCallback(
    (chefGroupId: number, column: MaterialLimitColumn) => {
      setWorkingLimits((current) => {
        const exists = (current ?? []).some(
          (limit) =>
            limit.chef_group_id === chefGroupId &&
            coefficientSignature(limit) === column.signature
        );
        if (exists) {
          return current;
        }
        const added: MaterialLimit = {
          chef_group_id: chefGroupId,
          coefficients: column.coefficients,
          name: column.name,
          hardness: 1,
          bounds: [],
        };
        return [...(current ?? []), added];
      });
    },
    []
  );

  // Editing a product-group aggregate writes to every chef group beneath it,
  // like selecting all its rows and using the bulk editor.
  const onGroupCellChange = useCallback(
    (
      chefGroupIds: number[],
      column: MaterialLimitColumn,
      field: MaterialLimitBoundField | "hardness",
      value: number | null
    ) => {
      const targetScope = field === "hardness" ? null : effectiveScope;
      setWorkingLimits((current) => {
        const limits = [...(current ?? [])];
        for (const chefGroupId of chefGroupIds) {
          const index = limits.findIndex(
            (limit) =>
              limit.chef_group_id === chefGroupId &&
              coefficientSignature(limit) === column.signature
          );
          const existing = index >= 0 ? limits[index] : undefined;
          if (existing === undefined && value == null) {
            continue;
          }
          const working = withUpdatedField(
            existing ?? {
              chef_group_id: chefGroupId,
              coefficients: column.coefficients,
              name: column.name,
              hardness: 1,
              bounds: [],
            },
            targetScope,
            field,
            value
          );
          if (existing !== undefined) {
            limits[index] = working;
          } else {
            limits.push(working);
          }
        }
        return limits;
      });
    },
    [effectiveScope]
  );

  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>(() => ({
      type: "include",
      ids: new Set<GridRowId>(),
    }));
  const selectedChefGroupIds = useMemo(() => {
    const numeric = [...rowSelectionModel.ids].filter(
      (id): id is number => typeof id === "number"
    );
    if (rowSelectionModel.type === "exclude") {
      const excluded = new Set(numeric);
      return new Set(
        rows.map((row) => row.id).filter((id) => !excluded.has(id))
      );
    }
    return new Set(numeric);
  }, [rowSelectionModel, rows]);

  // Limit-column filter (matches against the translated name and signature).
  const [columnFilter, setColumnFilter] = useState("");
  const visibleColumns = useMemo(() => {
    const query = columnFilter.trim().toLowerCase();
    if (query === "") {
      return columns;
    }
    return columns.filter((column) =>
      `${t(column.name)} ${column.name} ${column.coefficientsSignature}`
        .toLowerCase()
        .includes(query)
    );
  }, [columns, columnFilter, t]);

  // Bulk editor (surfaces only when more than one chef group is selected). It
  // edits the mix and every per-basket bound at once, prefilled with the value
  // the selected groups share when they agree.
  const bulkSerialiser = useNumberSerialiser({
    min: 0,
    default: { value: null, text: "" },
    decimalPlaces: 3,
  });
  const [bulkSignature, setBulkSignature] = useState<string>("");
  const bulkColumn =
    visibleColumns.find((column) => column.signature === bulkSignature) ??
    visibleColumns[0];
  const bulkScopes = useMemo<MaterialLimitScope[]>(
    () => [null, ...basketIds],
    [basketIds]
  );
  const selectedBulkLimits = useMemo(
    () =>
      bulkColumn === undefined
        ? []
        : (workingLimits ?? []).filter(
            (limit) =>
              selectedChefGroupIds.has(limit.chef_group_id) &&
              coefficientSignature(limit) === bulkColumn.signature
          ),
    [workingLimits, selectedChefGroupIds, bulkColumn]
  );
  const sharedBulk = useMemo(() => {
    const bounds: Record<
      string,
      Record<MaterialLimitBoundField, number | null>
    > = {};
    for (const scope of bulkScopes) {
      const fields: Record<MaterialLimitBoundField, number | null> = {
        min_mass: null,
        soft_min_mass: null,
        soft_max_mass: null,
        max_mass: null,
      };
      for (const field of MATERIAL_LIMIT_BOUND_FIELDS) {
        const values = new Set(
          selectedBulkLimits.map(
            (limit) => boundForScope(limit, scope)?.[field] ?? null
          )
        );
        fields[field] = values.size === 1 ? ([...values][0] ?? null) : null;
      }
      bounds[scope === null ? "mix" : String(scope)] = fields;
    }
    const hardnessValues = new Set(
      selectedBulkLimits.map((limit) => limit.hardness)
    );
    return {
      bounds,
      hardness:
        hardnessValues.size === 1 ? ([...hardnessValues][0] ?? null) : null,
    };
  }, [selectedBulkLimits, bulkScopes]);

  const [bulkBounds, setBulkBounds] = useState<
    Record<string, Record<MaterialLimitBoundField, number | null>>
  >({});
  const [bulkHardness, setBulkHardness] = useState<number | null>(null);
  // Prefill with the shared values when the selection or chosen column changes
  // (not on every edit), via the render-time "adjust state on prop change".
  const bulkKey =
    bulkColumn === undefined
      ? null
      : `${[...selectedChefGroupIds].sort((a, b) => a - b).join(",")}|${bulkColumn.signature}`;
  const [bulkSyncedKey, setBulkSyncedKey] = useState<string | null>(null);
  if (bulkSyncedKey !== bulkKey) {
    setBulkSyncedKey(bulkKey);
    setBulkBounds(sharedBulk.bounds);
    setBulkHardness(sharedBulk.hardness);
  }

  const applyBulk = useCallback(() => {
    if (bulkColumn === undefined) return;
    const scopeUpdates: {
      scope: MaterialLimitScope;
      field: MaterialLimitBoundField;
      value: number;
    }[] = [];
    for (const scope of bulkScopes) {
      const bound = bulkBounds[scope === null ? "mix" : String(scope)];
      if (bound === undefined) continue;
      for (const field of MATERIAL_LIMIT_BOUND_FIELDS) {
        const value = bound[field];
        if (value != null) {
          scopeUpdates.push({ scope, field, value });
        }
      }
    }
    if (scopeUpdates.length === 0 && bulkHardness == null) return;
    setWorkingLimits((current) => {
      const limits = [...(current ?? [])];
      for (const chefGroupId of selectedChefGroupIds) {
        const index = limits.findIndex(
          (limit) =>
            limit.chef_group_id === chefGroupId &&
            coefficientSignature(limit) === bulkColumn.signature
        );
        const existing = index >= 0 ? limits[index] : undefined;
        let working: MaterialLimit = existing ?? {
          chef_group_id: chefGroupId,
          coefficients: bulkColumn.coefficients,
          name: bulkColumn.name,
          hardness: 1,
          bounds: [],
        };
        for (const update of scopeUpdates) {
          working = withUpdatedField(
            working,
            update.scope,
            update.field,
            update.value
          );
        }
        if (bulkHardness != null) {
          working = withUpdatedField(working, null, "hardness", bulkHardness);
        }
        if (existing !== undefined) {
          limits[index] = working;
        } else {
          limits.push(working);
        }
      }
      return limits;
    });
  }, [bulkColumn, bulkScopes, bulkBounds, bulkHardness, selectedChefGroupIds]);

  const onApplyScrapchefDefaults = useCallback(() => {
    setWorkingLimits(Object.values(scrapchefLimitsByKey).map(toMaterialLimit));
  }, [scrapchefLimitsByKey]);

  const [revertOpen, setRevertOpen] = useState(false);
  const matchesScrapchefDefaults = useMemo(() => {
    const working = workingLimits ?? [];
    if (working.length !== Object.keys(scrapchefLimitsByKey).length) {
      return false;
    }
    return working.every((limit) => {
      const scrapchef = scrapchefLimitsByKey[materialLimitKey(limit)];
      return (
        scrapchef !== undefined &&
        !differsFromScrapchefDefault(limit, scrapchef)
      );
    });
  }, [workingLimits, scrapchefLimitsByKey]);
  const canRevert =
    Object.keys(scrapchefLimitsByKey).length > 0 && !matchesScrapchefDefaults;
  const bulkDisabled = selectedChefGroupIds.size < 2;

  const onDiscard = useCallback(() => {
    setWorkingLimits(originalLimits);
    setRowSelectionModel({ type: "include", ids: new Set() });
  }, [originalLimits]);

  const sourceSetId = activeSet?.id ?? null;
  const onSave = useCallback(async () => {
    if (sourceSetId === null || workingLimits === null || !hasChanges) {
      setConfirmOpen(false);
      return;
    }
    try {
      await updateDefaults({
        tenantName,
        updateDefaultMaterialLimitsRequest: {
          source_material_limit_set_id: sourceSetId,
          material_limits: workingLimits.filter(
            (limit) => !isMaterialLimitNullified(limit)
          ),
        },
      }).unwrap();
      notifyStatus({ text: t("defaultsSaved"), type: "success" });
      setRowSelectionModel({ type: "include", ids: new Set() });
      await refetch();
    } catch (error) {
      if (
        isFetchBaseQueryError(error) &&
        typeof error.status === "number" &&
        error.status === 409
      ) {
        notifyStatus({ text: t("defaultsOutOfDate"), type: "error" });
        void refetch();
      } else {
        notifyStatus({ text: t("errorSavingChanges"), type: "error" });
      }
    } finally {
      setConfirmOpen(false);
    }
  }, [
    sourceSetId,
    workingLimits,
    hasChanges,
    tenantName,
    updateDefaults,
    notifyStatus,
    refetch,
    t,
    setConfirmOpen,
  ]);

  useEffect(() => {
    if (!isActive) return;
    setTabActions({
      hasChanges,
      isChecking,
      isSaving,
      conflicts,
      onSave,
      onDiscard,
    });
    return () => {
      setTabActions(null);
    };
  }, [
    isActive,
    hasChanges,
    isChecking,
    isSaving,
    conflicts,
    onSave,
    onDiscard,
    setTabActions,
  ]);

  if (isError) {
    return <Alert severity="error">{t("errorLoadingData")}</Alert>;
  }

  return (
    <Stack gap={2} sx={{ flex: 1, minHeight: 0 }}>
      <Stack direction="row" gap={2} alignItems="stretch" flexWrap="wrap">
        <MaterialLimitLegend />
        {bulkColumn !== undefined ? (
          <Stack
            gap={1}
            sx={{
              p: 1.5,
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              flex: 1,
              minWidth: 320,
            }}
          >
            <Stack
              direction="row"
              gap={1.5}
              alignItems="center"
              flexWrap="wrap"
            >
              <Typography variant="subtitle2">{t("bulkSetBound")}</Typography>
              <Select
                size="small"
                disabled={bulkDisabled}
                value={bulkColumn.signature}
                onChange={(event) => {
                  setBulkSignature(event.target.value);
                }}
              >
                {visibleColumns.map((column) => (
                  <MenuItem key={column.signature} value={column.signature}>
                    {column.isExplicitlyNamed
                      ? t(column.name)
                      : formatSignatureLabel({
                          coefficients: column.coefficients,
                          classNameById,
                          translate: t,
                        })}
                  </MenuItem>
                ))}
              </Select>
              <Button
                variant="outlined"
                disabled={bulkDisabled}
                onClick={applyBulk}
              >
                {t("applyToSelected", { count: selectedChefGroupIds.size })}
              </Button>
              {bulkDisabled ? (
                <Typography variant="caption" color="text.secondary">
                  {t("selectRowsToBulkEdit")}
                </Typography>
              ) : null}
            </Stack>
            <Stack direction="row" gap={3} alignItems="flex-start">
              <Stack gap={1}>
                {bulkScopes.map((scope) => {
                  const key = scope === null ? "mix" : String(scope);
                  return (
                    <Stack
                      key={key}
                      direction="row"
                      gap={1}
                      alignItems="center"
                    >
                      <Typography variant="caption" sx={{ width: 72 }}>
                        {scope === null
                          ? t("mix")
                          : `${t("basket")} ${basketNumberById[scope] ?? ""}`}
                      </Typography>
                      <InequalityLayout
                        fields={MATERIAL_LIMIT_BOUND_FIELDS}
                        renderSlot={(field) => (
                          <ValidatedTextField
                            value={bulkBounds[key]?.[field] ?? null}
                            setValue={(next) => {
                              setBulkBounds((current) => ({
                                ...current,
                                [key]: {
                                  min_mass: null,
                                  soft_min_mass: null,
                                  soft_max_mass: null,
                                  max_mass: null,
                                  ...current[key],
                                  [field]: next,
                                },
                              }));
                            }}
                            serialiser={boundSerialiser}
                            textFieldProps={{
                              variant: "standard",
                              disabled: bulkDisabled,
                              sx: slotInputSx,
                            }}
                          />
                        )}
                      />
                    </Stack>
                  );
                })}
              </Stack>
              <Stack direction="row" gap={1} alignItems="center">
                <Typography variant="caption">{t("hardness")}</Typography>
                <Box
                  sx={{
                    border: 1,
                    borderColor: "primary.main",
                    borderRadius: 1,
                    px: 0.5,
                    display: "inline-flex",
                  }}
                >
                  <ValidatedTextField
                    value={bulkHardness}
                    setValue={setBulkHardness}
                    serialiser={bulkSerialiser}
                    textFieldProps={{
                      variant: "standard",
                      disabled: bulkDisabled,
                      sx: slotInputSx,
                    }}
                  />
                </Box>
              </Stack>
            </Stack>
          </Stack>
        ) : null}
      </Stack>
      <ScrapChefDefaultsWarning
        showWarning={showScrapchefWarning}
        onApplyDefaults={onApplyScrapchefDefaults}
      />
      <ConflictWarningBanner
        hasError={feasibilityError}
        conflicts={conflicts}
        isChecking={isChecking}
        chefGroupNameById={chefGroupNameById}
        limitLabelBySignature={limitLabelBySignature}
      />
      <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
        <Stack direction="row" gap={1} alignItems="center">
          <Typography variant="body2">{t("scope")}</Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={
              hardnessView
                ? "hardness"
                : effectiveScope === null
                  ? "mix"
                  : String(effectiveScope)
            }
            onChange={(_event, next: string | null) => {
              if (next === null) {
                return;
              }
              if (next === "hardness") {
                setHardnessView(true);
                return;
              }
              setHardnessView(false);
              setScope(next === "mix" ? null : Number(next));
            }}
          >
            <ToggleButton value="mix">{t("mix")}</ToggleButton>
            {basketIds.map((id) => (
              <ToggleButton key={id} value={String(id)}>
                {`${t("basket")} ${basketNumberById[id] ?? ""}`}
              </ToggleButton>
            ))}
            <ToggleButton
              value="hardness"
              sx={{
                ml: 1,
                color: "primary.main",
                "&.Mui-selected": {
                  color: "primary.contrastText",
                  bgcolor: "primary.main",
                  "&:hover": { bgcolor: "primary.dark" },
                },
              }}
            >
              {t("hardness")}
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <Stack direction="row" gap={1} alignItems="center">
          <Typography variant="body2">{t("units")}</Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={effectiveMeasure}
            onChange={(_event, next: MaterialLimitMeasure | null) => {
              if (next !== null) {
                setMeasure(next);
              }
            }}
          >
            <ToggleButton value="mass">
              {units(measureUnitType("mass"), false)}
            </ToggleButton>
            <ToggleButton value="small_mass">
              {units(measureUnitType("small_mass"), false)}
            </ToggleButton>
            {percentAvailable ? (
              <ToggleButton value="percent">
                {units(measureUnitType("percent"), false)}
              </ToggleButton>
            ) : null}
          </ToggleButtonGroup>
        </Stack>
        <TextField
          size="small"
          placeholder={t("filterColumns")}
          value={columnFilter}
          onChange={(event) => {
            setColumnFilter(event.target.value);
          }}
        />
        <Button
          size="small"
          variant="outlined"
          color="secondary"
          startIcon={<RestartAltIcon />}
          disabled={!canRevert}
          onClick={() => {
            setRevertOpen(true);
          }}
          sx={{ ml: "auto" }}
        >
          {t("revertToScrapChefDefault")}
        </Button>
      </Stack>
      {!hardnessView && effectiveScope !== null ? (
        <Alert severity="info">
          {t("basketFeasibilityScope", {
            basket: basketNumberById[effectiveScope],
          })}
        </Alert>
      ) : null}
      <MaterialLimitsGrid
        limitLabelBySignature={limitLabelBySignature}
        rows={rows}
        columns={visibleColumns}
        conflicts={conflictIndex}
        activeFieldsBySignature={activeFieldsBySignature}
        classNameById={classNameById}
        scope={effectiveScope}
        hardnessView={hardnessView}
        basketNumberById={basketNumberById}
        boundSerialiser={boundSerialiser}
        editedKeys={editedIds}
        scrapchefLimitsByKey={scrapchefLimitsByKey}
        loading={isLoading || isFetching}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={setRowSelectionModel}
        onCellChange={onCellChange}
        onCellAdd={onCellAdd}
        onGroupCellChange={onGroupCellChange}
      />
      <Dialog
        open={revertOpen}
        onClose={() => {
          setRevertOpen(false);
        }}
      >
        <DialogTitle>{t("revertToScrapChefTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("revertToScrapChefWarning")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRevertOpen(false);
            }}
          >
            {t("cancel")}
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              onApplyScrapchefDefaults();
              setRevertOpen(false);
            }}
          >
            {t("confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
